import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import NotificationBadge from './NotificationBadge';
import type { Info } from '../client/claims/types.gen';
import type { Partner } from '../client/portal/types.gen';
import { getPartnerInfoService } from '../services/portalServices';

type PartnerItemProps = {
  config: { logo: string; name: string };
  index: number;
  partner: Partner;
  setHasOpenItems: (index: number, hasOpenItem: boolean) => void;
  setTooltipOptions: (options: {
    content: JSX.Element | null;
    show: boolean;
    offsetTop: number;
  }) => void;
};

const PartnerItem = forwardRef(
  (
    {
      index,
      partner,
      config,
      setTooltipOptions,
      setHasOpenItems,
    }: PartnerItemProps,
    ref: React.Ref<HTMLLIElement>,
  ) => {
    const [info, setInfo] = useState<Info | null>(null);
    const [isMouseOver, setIsMouseOver] = useState(false);

    const itemRef = useRef<HTMLDivElement>(null);

    const getInfo = useCallback(
      async () => setInfo(await getPartnerInfoService(partner.apiBaseUrl)),
      [partner.apiBaseUrl],
    );

    const {
      fnolInboxCount,
      unlabeledEmailCount,
      unhandledClaimcaseEmailCount,
      unassignedClaimCasesCount,
    } = info
      ? info
      : {
          fnolInboxCount: 0,
          unassignedClaimCasesCount: 0,
          unlabeledEmailCount: 0,
          unhandledClaimcaseEmailCount: 0,
        };

    useEffect(() => {
      if (isMouseOver) {
        const timer = setTimeout(() => {
          if (itemRef.current) {
            const rect = itemRef.current.getBoundingClientRect();

            setTooltipOptions({
              show: true,
              content: (
                <>
                  <span>
                    {config.name === 'default' ? partner.name : config.name}
                  </span>
                  <br />
                  FNOL inbox: {fnolInboxCount}
                  <br />
                  Unassigned claim cases: {unassignedClaimCasesCount}
                  <br />
                  Unlabeled emails {unlabeledEmailCount}
                  <br />
                  Unread emails {unhandledClaimcaseEmailCount}
                </>
              ),
              offsetTop: rect.top + itemRef.current.clientHeight / 2,
            });
          }
        }, 500);

        return () => clearTimeout(timer);
      }
    }, [
      isMouseOver,
      config.name,
      partner.name,
      setTooltipOptions,
      fnolInboxCount,
      unassignedClaimCasesCount,
      unlabeledEmailCount,
      unhandledClaimcaseEmailCount,
    ]);

    useEffect(() => {
      getInfo();
    }, [getInfo]);

    useEffect(() => {
      const pollData = setInterval(getInfo, 60 * 1000);

      return () => clearInterval(pollData);
    }, [getInfo]);

    const handleMouseEnter = () => setIsMouseOver(true);

    const handleMouseLeave = () => {
      setIsMouseOver(false);
      setTooltipOptions({ show: false, content: null, offsetTop: 0 });
    };

    // `setHasOpenItems` is deliberately not a dependency: PartnersPanel
    // recreates it on every render, and it triggers a render there, so listing
    // it would loop.
    useEffect(() => {
      setHasOpenItems(
        index,
        fnolInboxCount +
          unlabeledEmailCount +
          unhandledClaimcaseEmailCount +
          unassignedClaimCasesCount >
          0,
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      index,
      fnolInboxCount,
      unlabeledEmailCount,
      unhandledClaimcaseEmailCount,
      unassignedClaimCasesCount,
    ]);

    return (
      <Item
        ref={ref}
        data-tip
        data-for={partner.name}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Link
          to={`/partner?url=${encodeURIComponent(partner.frontendBaseUrl)}`}
        >
          <ImageWrapper ref={itemRef}>
            <img src={config.logo} alt={config.name} />
            {config.name === 'default' && (
              <PartnerName>{partner.name}</PartnerName>
            )}
          </ImageWrapper>{' '}
        </Link>
        {info && info.fnolInboxCount + info.unassignedClaimCasesCount > 0 && (
          <NotificationBadge
            numberOfItems={info.fnolInboxCount + info.unassignedClaimCasesCount}
            position='top'
            color='red'
          />
        )}
        {info &&
          info.unlabeledEmailCount + info.unhandledClaimcaseEmailCount > 0 && (
            <NotificationBadge
              numberOfItems={
                info.unlabeledEmailCount + info.unhandledClaimcaseEmailCount
              }
              position='bottom'
              color='blue'
            />
          )}
      </Item>
    );
  },
);

export default PartnerItem;

const Item = styled.li`
  position: relative;
  user-select: none;
  transform: scale(1);
  transition: 0.2s linear;

  cursor: pointer;

  img {
    width: 2rem;
    height: 2rem;
  }

  &:hover {
    transform: scale(1.15);
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 2.8rem;
  height: 2.8rem;
  border: 0.125rem solid #cfcfcf;
  border-radius: 0.5rem;
  background-image: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0),
    rgba(255, 255, 255, 0.7)
  );
`;

const PartnerName = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  text-align: center;
  color: #fff;
  font-size: 0.625rem;
  height: 0.8rem;
  background-color: rgba(0, 0, 0, 0.4);
  border-bottom-left-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 0.1rem;
`;
